import { ReactNode, useEffect, useRef, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";

interface LineClampedTooltipProps {
  children: ReactNode;
  tooltipContent?: ReactNode;
  className?: string;
}

export const LineClampedTooltip = ({
  children,
  tooltipContent,
  className,
}: LineClampedTooltipProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const update = () => setIsClamped(node.scrollHeight > node.clientHeight);
    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [children]);

  const hasTooltip = isClamped || Boolean(tooltipContent);

  return (
    <Tooltip disabled={!hasTooltip}>
      <TooltipTrigger
        render={
          <div
            ref={ref}
            tabIndex={hasTooltip ? 0 : undefined}
            className={cn(
              "line-clamp-2 rounded-sm focus-visible:ring-3 focus-visible:ring-focus/50 focus-visible:outline-none",
              className,
            )}
          >
            {children}
          </div>
        }
      />
      <TooltipContent>{tooltipContent || children}</TooltipContent>
    </Tooltip>
  );
};
